import { Grid, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { continueBtn, goToClaimsBtn } from '../../../Constants/Constants';
import { IconStateType } from '../../../types/type';
import { getNextView, getStagesInOrder } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ConfirmationHeader from '../../shared/ConfirmationHeader/ConfirmationHeader';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

interface PhotoConfirmation {
  numberUploaded: number;
  isStandalone: boolean;
}

export default function PhotoServicesConfirmation() {
  const theme = useTheme();
  const { state } = useLocation();
  const { numberUploaded, isStandalone } = state as PhotoConfirmation;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);
  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) =>
        currentStage.length > 0 &&
        stage.id !== currentStage[0].id &&
        !stage.completed &&
        stage.isEligible &&
        !stage.skipped
    )
  )[0];

  async function handleNext() {
    //Update the current stage state and prepare the next stage state.
    if (!isStandalone) {
      const updatedStages = availableProgressStages.map((stage) => {
        stage =
          stage.id === currentStage[0].id
            ? {
                ...stage,
                completed: true,
                viewActive: false,
                showProgress: true,
                skipped: false,
              }
            : stage;
        stage =
          stage.id === nextStage.id
            ? {
                ...stage,
                completed: false,
                viewActive: true,
                showProgress: false,
              }
            : stage;
        return stage;
      });
      dispatch(updateStageState({ updatedStages: updatedStages }));
      navigate(getNextView(nextStage.id));
    }
  }

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }
  const numPhotos = numberUploaded === 1 ? '1 photo' : `${numberUploaded} photos`;

  const headerContent = 'You uploaded ' + numPhotos + ' to your claim.';
  const servicesContent = (
    <>
      {isStandalone && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader headerContent={headerContent} state={IconStateType.SUCCESS_STATE}></ConfirmationHeader>
        </Grid>

        <Grid sx={{ paddingTop: '2em', paddingBottom: '1rem' }} item xs={12}>
          <Typography sx={theme.custom.styles.confirmationSubHeader}>Next Steps</Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1">
            Your photos are being processed. You will be able to view them from your claim soon.
          </Typography>
        </Grid>

        <Grid sx={{ marginTop: '3em' }} item xs={12}>
          <CallToAction
            nextButton={!isStandalone}
            nextButtonContent={continueBtn}
            handleNext={handleNext}
            backButton={false}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </>
  );

  return <Box sx={{ width: '100%' }}>{servicesContent}</Box>;
}
